import { useTranslations } from "@jugl-web/utils";
import React, { useMemo } from "react";
import { useRestApiProvider, WhatsappQualityRating } from "@jugl-web/rest-api";
import { useEntitySelectedProvider } from "@web-src/modules/entities/providers/EntityProvider/useEntitySelectedProvider";
import { DataLoadingWrapper } from "@jugl-web/ui-kit";
import {
  CRMAccountCard,
  CRMAccountCardProps,
} from "../../../../components/CRMAccountCard";
import { ReactComponent as HighIcon } from "./assets/high.svg";
import { ReactComponent as MediumIcon } from "./assets/medium.svg";
import { ReactComponent as LowIcon } from "./assets/low.svg";
import { ReactComponent as UserIcon } from "./assets/user.svg";
import { ReactComponent as UnknownIcon } from "./assets/unknown.svg";
import { CRMInfoSection } from "../../../../components/CRMInfoSection";
import { getQualityRatingDetails } from "../../../../utils/getQualityRatingDetails";

export const WhatsappQualityRatingTab: React.FC = () => {
  const { t } = useTranslations();
  const { entityId } = useEntitySelectedProvider();
  const { integrationsApi } = useRestApiProvider();

  const { data, isLoading, isFetching, isError, refetch } =
    integrationsApi.useGetWhatsappQualityRatingQuery({ entityId });

  const cardProps: CRMAccountCardProps | undefined = useMemo(() => {
    if (!data) return undefined;

    const { description, status } = getQualityRatingDetails(data.rating, t);

    const ratingTitleIconMap: Record<
      WhatsappQualityRating,
      { title: string; icon: React.FC<React.SVGProps<SVGSVGElement>> }
    > = {
      GREEN: {
        title: t({
          id: "client-support-page.quality-rating-high",
          defaultMessage: "Quality Rating is High",
        }),
        icon: HighIcon,
      },
      YELLOW: {
        title: t({
          id: "client-support-page.quality-rating-medium",
          defaultMessage: "Quality Rating is Medium",
        }),
        icon: MediumIcon,
      },
      RED: {
        title: t({
          id: "client-support-page.quality-rating-low",
          defaultMessage: "Quality Rating is Low",
        }),
        icon: LowIcon,
      },
      UNKNOWN: {
        title: t({
          id: "client-support-page.quality-rating-unknown",
          defaultMessage: "Quality Rating is Unknown",
        }),
        icon: UnknownIcon,
      },
    };

    const { title, icon } = ratingTitleIconMap[data.rating];

    return {
      title,
      icon,
      description,
      status,
    };
  }, [data, t]);

  return (
    <DataLoadingWrapper
      isError={isError}
      isLoading={isLoading || isFetching}
      onRetry={refetch}
    >
      {cardProps && <CRMAccountCard {...cardProps} />}
      <CRMInfoSection
        title={t({
          id: "client-support-page.tools-and-tips",
          defaultMessage: "Tools & Tips",
        })}
        subtitle={t({
          id: "client-support-page.everything-you-need-to-know",
          defaultMessage: "Everything you need to know:",
        })}
        items={[
          {
            icon: <UserIcon />,
            title: t({
              id: "client-support-page.maintain-high-quality-rating",
              defaultMessage:
                "To maintain a high quality rating, please ensure:",
            }),
            description: t({
              id: "client-support-page.quality-rating-restore",
              defaultMessage: `If your quality rating becomes low, it shall be restored to the default rating "High" within 7 days.`,
            }),
            tips: [
              t({
                id: "client-support-page.send-messages-to-users-opted-in",
                defaultMessage: "Only send messages to users who have opted in",
              }),
              t({
                id: "client-support-page.dont-send-spammy-messages",
                defaultMessage: "Do not send spammy messages",
              }),
              t({
                id: "client-support-page.allow-users-opt-out-your-messages",
                defaultMessage:
                  "Allow your users to opt-out from your messages",
              }),
            ],
          },
        ]}
      />
    </DataLoadingWrapper>
  );
};
